import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'

import { Button, Divider, ListItemIcon, Menu, MenuItem } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import { Staff } from '@/core/models/staff.model'
import { useDeleteNetworkMutation, useGetNetworkMembersQuery, useGetNetworkQuery } from '@/core/services/network'
import { CreateGroupChat } from '@/pages/chat/ui/CreateGroupChat'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getApiErrorMessage } from '@/utils/errors'
import _ from 'lodash'

type MoreActionMenuProps = Readonly<{
  networkId: string
}>

export default function MoreActionMenu(props: MoreActionMenuProps) {
  const { networkId } = props

  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const authUser = useAppSelector(selectAuthUser)

  const { data: network, refetch: refetchNetwork } = useGetNetworkQuery(networkId, { refetchOnMountOrArgChange: true })
  const { data } = useGetNetworkMembersQuery(networkId, { refetchOnMountOrArgChange: true })
  const [deleteNetwork, { isLoading: isDeletingNetwork }] = useDeleteNetworkMutation()

  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const networkMembers = useMemo(() => _.filter(data, (item) => !!item?.member), [data])

  return (
    <>
      <PopupState variant="popover" popupId="network-more-action-menu">
        {(popupState) => (
          <>
            <Button {...bindTrigger(popupState)} color="inherit" variant="contained" sx={{ '&:hover': { bgcolor: (theme) => theme.palette.grey[300] } }}>
              <MoreHorizIcon />
            </Button>
            <Menu
              {...bindMenu(popupState)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    minWidth: 220,
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
            >
              <MenuItem component={Link} to={`/networks/${networkId}/member-requests`}>
                <ListItemIcon>
                  <PersonAddAltIcon />
                </ListItemIcon>
                {t('page.network.viewMemberRequest.label')}
              </MenuItem>
              {!network?.chatRoom && authUser && (
                <>
                  <Divider />
                  <CreateGroupChat
                    onGroupCreated={() => {
                      refetchNetwork()
                    }}
                    networkId={networkId}
                    creatorId={authUser._id}
                    staffs={(networkMembers?.map((nm) => nm.member) as Staff[]) ?? []}
                  />
                </>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  setOpenDeleteConfirmationDialog(true)
                  popupState.close()
                }}
              >
                <ListItemIcon>
                  <DeleteOutlineIcon />
                </ListItemIcon>
                {t('common:button.delete.label')}
              </MenuItem>
            </Menu>
          </>
        )}
      </PopupState>
      <DeleteConfirmationDialog
        isDeleting={isDeletingNetwork}
        open={openDeleteConfirmationDialog}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          try {
            await deleteNetwork(networkId).unwrap()
            setOpenDeleteConfirmationDialog(false)
            navigate('/networks')
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.apiKey.delete.error')), { variant: 'error' })
          }
        }}
        title={t('dialog.deleteConfirmation.network.title')}
        message={t('dialog.deleteConfirmation.network.message')}
      />
    </>
  )
}
